import { type FC, useEffect, useMemo, useState } from 'react'

import { Box, Text } from '@factorialco/deprecated-design-system'
import { F0Button, F0Select } from '@factorialco/f0-react'
import { Delete } from '@factorialco/f0-react/icons/app'

import i18n from 'modules/core/lib/i18n'

import { safeAssetTranslateType } from 'modules/it_management/components/AssetsList/utils'
import { useAllAssetModels } from 'modules/job_catalog/hooks/useAllAssetModels'

import { SelectedEquipment } from './types'

interface EquipmentData {
  id: string
  brand: string
  name: string
  typeName: string
}

interface EquipmentSelectorProps {
  onAdd: (equipment: SelectedEquipment) => void
  selectedEquipment: SelectedEquipment[]
  onSelectionChange: (category: string, model: string, equipmentData?: EquipmentData) => void
  initialSelectedCategory?: string
  initialSelectedModel?: string
  restrictToCategory?: string
}

export const EquipmentSelector: FC<EquipmentSelectorProps> = ({
  onAdd: _onAdd,
  selectedEquipment: _selectedEquipment,
  onSelectionChange,
  initialSelectedCategory = '',
  initialSelectedModel = '',
  restrictToCategory,
}) => {
  const [_search, _setSearch] = useState('')
  const [selectedCategory, setSelectedCategory] = useState<string>(initialSelectedCategory)
  const [selectedModel, setSelectedModel] = useState<string>(initialSelectedModel)

  const { data: availableEquipment, loading, error } = useAllAssetModels()

  const categories = useMemo(() => {
    const uniqueCategories = Array.from(
      new Set(availableEquipment.map((equipment) => equipment.typeName))
    )

    // If restrictToCategory is specified, only show that category
    const filteredCategories = restrictToCategory
      ? uniqueCategories.filter((category) => category === restrictToCategory)
      : uniqueCategories

    return filteredCategories.map((category) => ({
      value: category,
      label: safeAssetTranslateType(category),
    }))
  }, [availableEquipment, restrictToCategory])

  const models = useMemo(() => {
    if (!selectedCategory) return []

    const categoryEquipment = availableEquipment.filter(
      (equipment) => equipment.typeName === selectedCategory
    )

    return categoryEquipment.map((equipment) => ({
      value: equipment.id,
      label: `${equipment.brand} ${equipment.name}`,
      data: equipment,
    }))
  }, [availableEquipment, selectedCategory])

  useEffect(() => {
    setSelectedCategory(initialSelectedCategory)
  }, [initialSelectedCategory, initialSelectedModel])

  useEffect(() => {
    if (initialSelectedModel && models.length > 0 && selectedCategory === initialSelectedCategory) {
      const modelExists = models.find((model) => model.value === initialSelectedModel)
      if (modelExists) {
        setSelectedModel(initialSelectedModel)
      }
    }
  }, [models, initialSelectedModel, initialSelectedCategory, selectedCategory])

  useEffect(() => {
    const selectedEquipmentData = models.find((model) => model.value === selectedModel)?.data
    onSelectionChange(selectedCategory, selectedModel, selectedEquipmentData)
  }, [selectedModel, selectedCategory, models, onSelectionChange])

  if (loading) {
    return (
      <Box gap="s20">
        <Text.Normal size="200" color="grey600">
          {i18n.t('common.loading')}
        </Text.Normal>
      </Box>
    )
  }

  if (error) {
    return (
      <Box gap="s20">
        <Text.Normal size="200" color="moodSuperNegative">
          {i18n.t('common.loading', { error: error.message })}
        </Text.Normal>
      </Box>
    )
  }

  return (
    <Box gap="s20">
      <Box flexDirection="row" gap="s12">
        <Box style={{ flex: '1' }}>
          <Box paddingBottom="s8">
            <Text.Normal size="200" weight="medium" color="grey700">
              {i18n.t('job_catalog.equipment.form.category')} *
            </Text.Normal>
          </Box>
          <F0Select
            label="Category"
            hideLabel
            placeholder={i18n.t('job_catalog.equipment.form.category_placeholder')}
            value={selectedCategory}
            onChange={setSelectedCategory}
            options={categories}
            size="md"
            disabled={!!restrictToCategory}
          />
        </Box>

        {/* Model selector */}
        <Box style={{ flex: '2' }}>
          <Box paddingBottom="s8">
            <Text.Normal size="200" weight="medium" color="grey700">
              {i18n.t('job_catalog.equipment.form.model')} *
            </Text.Normal>
          </Box>
          <F0Select
            label="Model"
            hideLabel
            placeholder={
              !selectedCategory
                ? i18n.t('job_catalog.equipment.form.model_placeholder_category_first')
                : i18n.t('job_catalog.equipment.form.model_placeholder')
            }
            value={selectedModel}
            onChange={setSelectedModel}
            options={models}
            disabled={!selectedCategory}
            size="md"
          />
        </Box>

        <Box style={{ alignSelf: 'flex-end' }}>
          <F0Button
            variant="outline"
            size="sm"
            icon={Delete}
            onClick={() => {
              setSelectedCategory('')
              setSelectedModel('')
            }}
            disabled={!selectedCategory && !selectedModel}
            style={{
              width: '40px',
              height: '40px',
              minWidth: '40px',
              padding: '0',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          />
        </Box>
      </Box>
    </Box>
  )
}
